import {
  AgBarSeriesOptions,
  AgCartesianChartOptions,
  AgChartOptions,
  AgSeriesVisibilityChange,
} from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'translations/hooks';
import { getAgChartLocale } from 'translations/locale';
import { Locale } from 'translations/types';

import { useSingleGraphReferenceContext } from 'business/providers/graph-reference';
import { useIndicatorAnalyzeFilters } from 'business/shift-report/indicators/pages/indicator-analyze/providers/filters';
import {
  IndicatorAnalyseLegendFilters,
  useLegendFilters,
} from 'business/shift-report/indicators/pages/indicator-analyze/providers/legend-filters';
import { IndicatorMetadataDto, IndicatorValuesDto } from 'generated/apiSchemas';
import { useChartResize } from 'technical/chart-resize';
import { disableLegendDoubleClick } from 'technical/graph/disable-legend-double-clidk';
import { hasInitialZoom } from 'technical/graph/has-initial-zoom';
import { useMediaType } from 'technical/media/hooks';

const getIndicatorName = (
  indicator: IndicatorMetadataDto,
  language: string,
) => {
  switch (language) {
    case Locale.en:
    case Locale.enGb:
      return indicator.nameEn;

    case Locale.es:
      return indicator.nameEs;

    case Locale.fr:
    default:
      return indicator.nameFr;
  }
};

const getBarChartOptions = (
  series: IndicatorMetadataDto[] | undefined,
  language: string,
  selectedSeries: IndicatorAnalyseLegendFilters,
) => {
  return {
    series: series?.map((serie) => {
      const indicatorName = getIndicatorName(serie, language);
      return {
        type: 'bar',
        xKey: 'group',
        yKey: serie.id,
        yName: indicatorName,
        visible:
          selectedSeries.allIndicators ||
          selectedSeries.selectedIndicators.includes(serie.id),
        strokeWidth: 1,
        legendItemName: `${indicatorName} (${serie.unit})`,
        tooltip: {
          renderer: (params) => {
            const tootlip = {
              data: [
                {
                  label: indicatorName,
                  value: `${Number(params.datum[params.yKey]).toLocaleString(
                    language,
                  )} ${serie.unit}`,
                },
              ],
            };
            return tootlip;
          },
        },
      } satisfies AgBarSeriesOptions;
    }),
    axes: [
      {
        type: 'category',
        position: 'bottom',
      },
      {
        type: 'number',
        position: 'left',
        nice: false,
      },
    ],
  } satisfies AgCartesianChartOptions;
};

const updateSelectedIndicators = (
  itemId: string,
  indicatorIds: string[],
  selectedIndicatorIds: string[],
  setSelectedIndicators: (
    value: SetStateAction<IndicatorAnalyseLegendFilters>,
  ) => void,
) => {
  const newLegendFilters = new Set(selectedIndicatorIds);
  if (newLegendFilters.has(itemId)) {
    newLegendFilters.delete(itemId);
  } else {
    newLegendFilters.add(itemId);
  }

  setSelectedIndicators({
    allIndicators: newLegendFilters.size === indicatorIds.length,
    selectedIndicators: Array.from(newLegendFilters),
  });
};

const updateVisibility =
  (
    indicators: IndicatorMetadataDto[] | undefined,
    legendFilters: IndicatorAnalyseLegendFilters,
    setLegendFilters: Dispatch<SetStateAction<IndicatorAnalyseLegendFilters>>,
  ) =>
  ({ itemId }: AgSeriesVisibilityChange) => {
    if (itemId && typeof itemId === 'string') {
      const indicatorIds = indicators?.map((indicator) => indicator.id) ?? [];
      if (legendFilters.allIndicators) {
        updateSelectedIndicators(
          itemId,
          indicatorIds,
          indicatorIds,
          setLegendFilters,
        );
        return;
      }

      updateSelectedIndicators(
        itemId,
        indicatorIds,
        legendFilters.selectedIndicators,
        setLegendFilters,
      );
    }
  };

export const IndicatorAnalyzeGraph = ({
  indicators,
  data,
}: {
  indicators: IndicatorMetadataDto[] | undefined;
  data: IndicatorValuesDto['aggregated'];
}) => {
  const { language } = useTranslation();
  const { ref: chartRef } = useSingleGraphReferenceContext();
  const filters = useIndicatorAnalyzeFilters();
  const { legendFilters, setLegendFilters } = useLegendFilters();

  const { isMobile } = useMediaType();
  const initialZoom = hasInitialZoom(isMobile, filters);

  const chartOptions = {
    data,
    ...getBarChartOptions(indicators, language, legendFilters),
    legend: {
      enabled: true,
      listeners: {
        legendItemDoubleClick: disableLegendDoubleClick,
      },
    },
    listeners: {
      seriesVisibilityChange: updateVisibility(
        indicators,
        legendFilters,
        setLegendFilters,
      ),
    },
    locale: {
      localeText: getAgChartLocale(language),
    },
    ...(isMobile
      ? {
          touch: { dragAction: 'drag' },
          zoom: {
            enabled: true,
            enableAxisDragging: false,
            enableTwoFingerZoom: true,
            buttons: {
              enabled: false,
            },
          },
          initialState: {
            zoom: initialZoom
              ? {
                  ratioX: {
                    start: 0,
                    end: 0.4,
                  },
                }
              : {},
          },
          contextMenu: {
            enabled: false,
          },
        }
      : {
          zoom: {
            // zoom on where the mouse is
            anchorPointX: 'pointer',
            anchorPointY: 'pointer',
          },
        }),
  } satisfies AgChartOptions;

  useChartResize(chartRef);

  return <AgCharts options={chartOptions} ref={chartRef} />;
};
